<template>
  <v-dialog
    max-width="400"
    @click:outside="resetParams"
  >
    <template #activator="{ on, attrs }">
      <v-btn
        color="primary"
        :ripple="false"
        :disabled="disabled"
        small
        v-bind="attrs"
        v-on="on"
      >
        新規作成
      </v-btn>
    </template>

    <template #default="dialog">
      <v-card>
        <v-card-title>新規作成</v-card-title>

        <v-card-text>
          <v-row>
            <v-col cols="12">
              <v-combobox
                :value="selectedUserName"
                label="ユーザー"
                :items="users"
                item-text="attributes.name"
                dense
                autofocus
                @change="onUserSelect"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="params.startAt"
                label="開始"
                type="datetime-local"
                dense
                :error-messages="startAtWarning ? [startAtWarning] : []"
                @focus="setDefaultDateTime('startAt')"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="params.leaveAt"
                label="終了"
                type="datetime-local"
                :min="params.startAt"
                :disabled="!params.startAt"
                dense
                :error-messages="leaveAtWarning ? [leaveAtWarning] : []"
                @focus="setDefaultDateTime('leaveAt')"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="params.lateMinutes"
                label="遅刻/分"
                type="number"
                dense
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="params.earlyLeaveMinutes"
                label="早退/分"
                type="number"
                dense
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="6">
              <v-select
                v-model="params.timecardStatusId"
                label="ステータス"
                :items="timecardStatuses"
                item-text="attributes.name"
                item-value="attributes.id"
                dense
                clearable
              />
            </v-col>
          </v-row>

          <v-row
            v-for="bra in applicableBras"
            :key="`bra-${bra.id}`"
          >
            <v-col cols="12">
              <v-text-field
                v-if="bra.attributes.businessRule.editableResult"
                v-model.number="businessRuleValues[bra.attributes.businessRule.id]"
                :label="bra.attributes.businessRule.name"
                type="number"
                dense
              />
              <v-text-field
                v-else
                :label="bra.attributes.businessRule.name"
                :value="businessRuleValues[bra.attributes.businessRule.id] != null ? businessRuleValues[bra.attributes.businessRule.id] : ''"
                :placeholder="businessRuleValues[bra.attributes.businessRule.id] == null ? '保存時にYAML計算' : ''"
                readonly
                dense
                class="text--disabled"
              />
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            small
            @click="close(dialog)"
          >
            キャンセル
          </v-btn>
          <v-btn
            color="primary"
            :disabled="isInvalid"
            small
            @click="create(dialog)"
          >
            新規作成
          </v-btn>
        </v-card-actions>
      </v-card>
    </template>
  </v-dialog>
</template>

<script>
import {
  toRefs,
  reactive,
  computed,
  watch,
  getCurrentInstance,
} from '@vue/composition-api'
import { format, parseISO, addDays } from 'date-fns'
import useBusinessDateAdjustment from '../composables/useBusinessDateAdjustment'

export default {
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    timecardStatuses: {
      type: Array,
      default: () => [],
    },
    users: {
      type: Array,
      default: () => [],
    },
    date: {
      type: String,
      default: null,
    },
    businessRuleAssignments: {
      type: Array,
      default: () => [],
    },
    existingBrrByUserId: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy
    const { users, date, businessRuleAssignments } = toRefs(props)
    const {
      currentClub, businessHours, adjustDateForMidnightCrossing, buildDefaultDateTime,
    } = useBusinessDateAdjustment(date)

    const params = reactive({
      userId: null,
      startAt: null,
      leaveAt: null,
      lateMinutes: 0,
      earlyLeaveMinutes: 0,
      timecardStatusId: null,
    })

    const businessRuleValues = reactive({})

    // Vue 2 のリアクティビティ制約対策: BRA ロード時に BR キーを事前登録
    watch(businessRuleAssignments, bras => {
      bras.forEach(bra => {
        const brId = bra.attributes.businessRule.id
        if (!(brId in businessRuleValues)) {
          vm.$set(businessRuleValues, brId, null)
        }
      })
    }, { immediate: true })

    // ユーザー切替時、選択したユーザーの既存BRR値を入力欄にプリロード
    watch(() => params.userId, userId => {
      Object.keys(businessRuleValues).forEach(key => {
        vm.$set(businessRuleValues, key, null)
      })
      if (!userId) return
      const existingBrrs = props.existingBrrByUserId?.[userId] || []
      existingBrrs.forEach(brr => {
        vm.$set(businessRuleValues, brr.businessRuleId, brr.resultValue)
      })
    })

    const getBusinessDayRange = () => {
      if (!date.value) return null

      // サーバ解決の営業日ウィンドウ(営業時間履歴を時点解決)を優先する。
      // 営業時間の切替営業日(長い日/短い日)や過去営業日の旧営業時間も正しく扱える
      const bh = businessHours.value
      if (bh?.beginning_of_day && bh?.end_of_day) {
        return { start: new Date(bh.beginning_of_day), end: new Date(bh.end_of_day) }
      }

      const closingDate = parseISO(date.value)
      const openAt = currentClub.value?.openAt

      if (openAt) {
        const openTime = parseISO(openAt)
        const openHour = openTime.getHours()
        const openMin = openTime.getMinutes()

        const start = new Date(closingDate)
        start.setHours(openHour, openMin, 0, 0)

        const end = addDays(start, 1)
        end.setSeconds(end.getSeconds() - 1)

        return { start, end }
      }

      // openAtがない場合はクロージング日 + 翌日をカバー
      const nextDate = addDays(closingDate, 1)

      return {
        start: closingDate,
        end: new Date(nextDate.getFullYear(), nextDate.getMonth(), nextDate.getDate(), 23, 59, 59),
      }
    }

    const isOutsideBusinessDay = datetime => {
      if (!datetime || !date.value) return false

      const range = getBusinessDayRange()
      if (!range) return false

      const inputDate = new Date(datetime)

      return inputDate < range.start || inputDate > range.end
    }

    const businessDayLabel = computed(() => {
      const range = getBusinessDayRange()
      if (!range) return ''

      return `${format(range.start, 'M/d HH:mm')} 〜 ${format(range.end, 'M/d HH:mm')}`
    })

    const startAtWarning = computed(() => {
      if (isOutsideBusinessDay(params.startAt)) {
        return `営業日の範囲外です（${businessDayLabel.value}）`
      }

      return ''
    })

    const leaveAtWarning = computed(() => {
      if (isOutsideBusinessDay(params.leaveAt)) {
        return `営業日の範囲外です（${businessDayLabel.value}）`
      }

      return ''
    })

    const selectedUserName = computed(() => {
      const { userId } = params
      if (userId) {
        return users.value.find(user => user.attributes.id === userId)?.attributes?.name
      }

      return ''
    })

    const applicableBras = computed(() => {
      if (!params.userId) return []

      return businessRuleAssignments.value.filter(bra => {
        const userIds = bra.attributes?.userIds || []

        return userIds.includes(params.userId)
      })
    })

    const hasBusinessRuleInput = computed(() => {
      return Object.values(businessRuleValues).some(
        value => value !== null && value !== undefined && value !== '',
      )
    })

    const isInvalid = computed(() => {
      const { userId, startAt } = params

      if (!userId) return true
      if (!startAt && !hasBusinessRuleInput.value) return true

      if (params.leaveAt && startAt) {
        const startDate = new Date(startAt)
        const leaveDate = new Date(params.leaveAt)

        // leaveAt が startAt より前の場合、無効
        if (leaveDate < startDate) return true
      }

      return false
    })

    watch(() => params.startAt, val => {
      if (!val) return
      const adjusted = adjustDateForMidnightCrossing(val)
      if (adjusted !== val) params.startAt = adjusted
    })

    watch(() => params.leaveAt, val => {
      if (!val || !params.startAt) return
      const leaveDate = new Date(val)
      if (leaveDate < new Date(params.startAt)) {
        params.leaveAt = format(addDays(leaveDate, 1), "yyyy-MM-dd'T'HH:mm")
      }
    })

    const setDefaultDateTime = key => {
      if (params[key]) return
      params[key] = buildDefaultDateTime(key, params.startAt)
    }

    const resetParams = () => {
      params.userId = null
      params.startAt = null
      params.leaveAt = null
      params.lateMinutes = 0
      params.earlyLeaveMinutes = 0
      params.timecardStatusId = null
      Object.keys(businessRuleValues).forEach(key => {
        vm.$set(businessRuleValues, key, null)
      })
    }

    const close = dialog => {
      // eslint-disable-next-line no-param-reassign
      dialog.value = false
      resetParams()
    }

    const create = dialog => {
      if (isInvalid.value) return

      const businessRuleResults = Object.entries(businessRuleValues)
        .filter(([, value]) => value !== null && value !== undefined && value !== '')
        .map(([businessRuleId, resultValue]) => ({
          businessRuleId: Number(businessRuleId),
          resultValue,
        }))

      const paramsToEmit = {
        ...params,
        ...(businessRuleResults.length > 0 && { businessRuleResults }),
      }
      emit('create', paramsToEmit)
      close(dialog)
    }

    const onUserSelect = item => {
      if (item?.attributes?.id) {
        params.userId = item.attributes.id
      }
    }

    return {
      params,
      businessRuleValues,
      selectedUserName,
      applicableBras,
      isInvalid,
      startAtWarning,
      leaveAtWarning,
      close,
      create,
      resetParams,
      onUserSelect,
      setDefaultDateTime,
    }
  },
}
</script>
